import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, ChevronUp, ChevronDown, ExternalLink, Eye,
  MessageSquare, ShieldCheck, ShieldAlert, Star, Quote,
} from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
  { key: "google_business", label: "Google Business Profile" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "direct", label: "Direct Client Testimonial" },
  { key: "other", label: "Other" },
];

const LANGUAGES = [
  { key: "both", label: "Both (EN + ES)" },
  { key: "en", label: "English only" },
  { key: "es", label: "Spanish only" },
];

const empty = {
  client_name: "", company_name: "", review_text: "", rating: "",
  source: "direct", source_url: "", review_date: "",
  screenshot_url: "", client_photo_url: "", company_logo_url: "",
  language: "both",
  verified: false, active: true,
};

export default function Testimonials() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/testimonials");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const publicRows = useMemo(() => rows.filter((r) => r.verified && r.active), [rows]);
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...empty,
      ...r,
      rating: r.rating ?? "",
      company_name: r.company_name || "",
      source_url: r.source_url || "",
      review_date: r.review_date || "",
      screenshot_url: r.screenshot_url || "",
      client_photo_url: r.client_photo_url || "",
      company_logo_url: r.company_logo_url || "",
      language: r.language || "both",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.client_name.trim() || !form.review_text.trim()) {
      toast.error("Client name and review text are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        company_name: form.company_name?.trim() || null,
        review_text: form.review_text.trim(),
        rating: form.rating === "" || form.rating === null ? null : Number(form.rating),
        source: form.source || "direct",
        source_url: form.source_url?.trim() || null,
        review_date: form.review_date || null,
        screenshot_url: form.screenshot_url?.trim() || null,
        client_photo_url: form.client_photo_url?.trim() || null,
        company_logo_url: form.company_logo_url?.trim() || null,
        language: form.language || "both",
        verified: !!form.verified,
        active: !!form.active,
      };
      if (editing) {
        await api.patch(`/testimonials/${editing.testimonial_id}`, payload);
        toast.success("Review updated");
      } else {
        await api.post("/testimonials", payload);
        toast.success("Review added");
      }
      setOpen(false); setEditing(null); setForm(empty);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from "${r.client_name}"?`)) return;
    try {
      await api.delete(`/testimonials/${r.testimonial_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const patch = async (r, changes) => {
    try {
      await api.patch(`/testimonials/${r.testimonial_id}`, changes);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(idx, 1);
    next.splice(t, 0, row);
    setRows(next);
    try {
      await api.post("/testimonials/reorder", { order: next.map((r) => r.testimonial_id) });
    } catch {
      toast.error("Reorder failed");
      await load();
    }
  };

  return (
    <DashboardLayout
      title="Reviews & Testimonials"
      subtitle="Manage the client reviews shown on the public homepage — only VERIFIED + ACTIVE reviews are published"
      right={
        <button data-testid="review-new" onClick={openNew}
          className="btn-scale h-9 px-3 rounded-md bg-blue-500 hover:bg-blue-400 text-white font-medium text-sm flex items-center gap-2 shadow-md shadow-blue-500/30">
          <Plus className="w-4 h-4" /> Add Review
        </button>
      }
    >
      <div className="space-y-6" data-testid="review-page">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <div className="col-span-1 text-center">Order</div>
            <div className="col-span-3">Client · Company</div>
            <div className="col-span-3">Review</div>
            <div className="col-span-1 text-center">Rating</div>
            <div className="col-span-1 text-center">Source</div>
            <div className="col-span-1 text-center">Lang</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="review-empty">
              <MessageSquare className="w-6 h-6 mx-auto mb-3 opacity-50" />
              No reviews yet. Add your first verified client review to publish on the homepage.
            </div>
          ) : rows.map((r, i) => (
            <div key={r.testimonial_id}
                 data-testid={`review-row-${r.testimonial_id}`}
                 className={`grid grid-cols-12 gap-3 px-4 py-3 items-start border-b border-slate-800/60 hover:bg-slate-800/30 ${!r.active ? "opacity-50" : ""}`}>
              <div className="col-span-1 flex flex-col items-center justify-center gap-1 pt-1">
                <button onClick={() => move(i, -1)} disabled={i === 0}
                        data-testid={`review-up-${r.testimonial_id}`}
                        className="w-6 h-6 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 disabled:opacity-30">
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                        data-testid={`review-down-${r.testimonial_id}`}
                        className="w-6 h-6 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 disabled:opacity-30">
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
              <div className="col-span-3">
                <div className="text-sm font-semibold text-slate-100">{r.client_name}</div>
                {r.company_name && <div className="text-xs text-slate-500">{r.company_name}</div>}
                {r.review_date && <div className="text-[10px] text-slate-600 mt-1">{r.review_date}</div>}
              </div>
              <div className="col-span-3 text-xs text-slate-300 line-clamp-3">{r.review_text}</div>
              <div className="col-span-1 text-center text-sm text-amber-400">
                {r.rating != null ? `★ ${Number(r.rating).toFixed(1)}` : <span className="text-slate-600">—</span>}
              </div>
              <div className="col-span-1 text-center">
                <span className="text-[10px] uppercase tracking-widest text-slate-400">{(r.source || "direct").replace("_", " ")}</span>
                {r.source_url && (
                  <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                     className="block text-blue-400 hover:text-blue-300 text-[10px] mt-0.5">
                    <ExternalLink className="w-3 h-3 inline" />
                  </a>
                )}
              </div>
              <div className="col-span-1 text-center">
                <span data-testid={`review-lang-${r.testimonial_id}`}
                      className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${
                        (r.language || "both") === "en" ? "border-blue-500/60 text-blue-300 bg-blue-500/10" :
                        (r.language || "both") === "es" ? "border-amber-500/60 text-amber-300 bg-amber-500/10" :
                        "border-slate-700 text-slate-300"
                      }`}>
                  {(r.language || "both") === "both" ? "EN+ES" : (r.language || "both").toUpperCase()}
                </span>
              </div>
              <div className="col-span-1 flex flex-col items-center gap-1">
                <button data-testid={`review-verify-${r.testimonial_id}`} onClick={() => patch(r, { verified: !r.verified })}
                        title={r.verified ? "Verified — click to unverify" : "Not verified — click to verify"}
                        className={`w-7 h-7 grid place-items-center rounded border ${r.verified ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10" : "border-slate-700 text-slate-500"}`}>
                  {r.verified ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                </button>
                <button data-testid={`review-toggle-${r.testimonial_id}`} onClick={() => patch(r, { active: !r.active })}
                        className={`w-9 h-5 rounded-full relative transition-colors ${r.active ? "bg-emerald-500/70" : "bg-slate-700"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${r.active ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>
              <div className="col-span-1 flex items-start justify-end gap-1 pt-1">
                <button data-testid={`review-edit-${r.testimonial_id}`} onClick={() => openEdit(r)}
                        className="w-7 h-7 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 text-slate-300">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`review-delete-${r.testimonial_id}`} onClick={() => remove(r)}
                        className="w-7 h-7 grid place-items-center rounded border border-slate-700 hover:border-rose-500/50 text-slate-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          <div className="px-4 py-3 text-[11px] text-slate-500 border-t border-slate-800 flex items-center justify-between">
            <span>{rows.length} total · {publicRows.length} verified &amp; active (published)</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> Only <b className="text-slate-300">verified &amp; active</b> reviews appear publicly</span>
          </div>
        </div>

        {/* Live preview */}
        <div className="rounded-xl border border-blue-500/20 overflow-hidden" data-testid="review-preview">
          <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-slate-100">Live preview — public reviews section</span>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-slate-500">{publicRows.length} published</span>
          </div>
          <TestimonialsPreview rows={publicRows} />
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Review" : "Add Review"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <F label="Client Name *" testid="review-input-client">
              <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                     className="ipt" data-testid="review-input-client" />
            </F>
            <F label="Company Name">
              <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                     className="ipt" data-testid="review-input-company" />
            </F>
            <div className="sm:col-span-2">
              <F label="Review Text *">
                <textarea required rows={4} value={form.review_text} onChange={(e) => setForm({ ...form, review_text: e.target.value })}
                          className="ipt py-2" data-testid="review-input-text" />
              </F>
            </div>
            <F label="Rating (0–5, optional)">
              <input type="number" min="0" max="5" step="0.1" value={form.rating}
                     onChange={(e) => setForm({ ...form, rating: e.target.value })}
                     placeholder="Leave blank if no rating" className="ipt" data-testid="review-input-rating" />
            </F>
            <F label="Source Platform">
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                      className="ipt" data-testid="review-input-source">
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </F>
            <F label="Original Review URL">
              <input type="url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                     placeholder="https://…" className="ipt" data-testid="review-input-source-url" />
            </F>
            <F label="Review Date">
              <input type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })}
                     className="ipt" data-testid="review-input-date" />
            </F>
            <F label="Screenshot URL (optional)">
              <input type="url" value={form.screenshot_url} onChange={(e) => setForm({ ...form, screenshot_url: e.target.value })}
                     placeholder="Paste image URL" className="ipt" data-testid="review-input-screenshot" />
            </F>
            <F label="Client Photo URL (optional)">
              <input type="url" value={form.client_photo_url} onChange={(e) => setForm({ ...form, client_photo_url: e.target.value })}
                     placeholder="Paste image URL" className="ipt" data-testid="review-input-photo" />
            </F>
            <F label="Company Logo URL (optional)">
              <input type="url" value={form.company_logo_url} onChange={(e) => setForm({ ...form, company_logo_url: e.target.value })}
                     placeholder="Paste image URL" className="ipt" data-testid="review-input-logo" />
            </F>
            <F label="Publish Language">
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                      className="ipt" data-testid="review-input-language">
                {LANGUAGES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </F>

            <div className="sm:col-span-2 flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <button type="button" onClick={() => setForm({ ...form, verified: !form.verified })}
                        data-testid="review-input-verified"
                        className={`w-9 h-5 rounded-full relative transition-colors ${form.verified ? "bg-emerald-500/70" : "bg-slate-700"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.verified ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
                <span className="text-sm text-slate-200">Verified — allow public display</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <button type="button" onClick={() => setForm({ ...form, active: !form.active })}
                        data-testid="review-input-active"
                        className={`w-9 h-5 rounded-full relative transition-colors ${form.active ? "bg-emerald-500/70" : "bg-slate-700"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.active ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
                <span className="text-sm text-slate-200">Active</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-4 rounded-md border border-slate-700 text-slate-200 text-sm">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="review-submit"
                    className="btn-scale h-9 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Save changes" : "Add review"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        .ipt { width:100%; height:2.5rem; background:#020617; border:1px solid #1e293b; border-radius:0.375rem; padding:0 0.75rem; font-size:0.875rem; color:#f1f5f9; outline:none; }
        .ipt:focus { border-color:#3b82f6; }
      `}</style>
    </DashboardLayout>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function TestimonialsPreview({ rows }) {
  if (!rows?.length) {
    return (
      <div className="py-10 text-center text-sm text-slate-500" style={{ background: "#0A1530" }}>
        <Quote className="w-5 h-5 inline mr-2 opacity-60" />
        No verified reviews yet — the homepage testimonials section is currently hidden.
      </div>
    );
  }
  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" style={{ background: "linear-gradient(180deg,#0A1530 0%,#0C1839 100%)" }}>
      {rows.slice(0, 4).map((r) => (
        <div key={r.testimonial_id} className="rounded-lg border border-blue-500/20 bg-slate-900/60 p-4">
          <div className="flex items-center gap-3 mb-2">
            {r.client_photo_url ? (
              <img src={r.client_photo_url} alt={r.client_name} className="w-10 h-10 rounded-full object-cover border border-slate-700" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-500/20 grid place-items-center text-blue-300 text-sm font-bold">
                {r.client_name?.[0]}
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-white">{r.client_name}</div>
              {r.company_name && <div className="text-xs text-slate-400">{r.company_name}</div>}
            </div>
            {r.rating != null && r.source !== "instagram" && (
              <div className="ml-auto flex items-center gap-0.5 text-amber-400 text-xs">
                <Star className="w-3.5 h-3.5 fill-current" /> {Number(r.rating).toFixed(1)}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-200 leading-relaxed line-clamp-4">{r.review_text}</p>
          <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest">
            <span className="text-slate-500">{(r.source || "direct").replace("_", " ")}</span>
            {r.source_url && (
              <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                 className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                View Original <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
